import React, { useEffect, useRef } from "react";

import Layout from "../../../components/reusable/Layout";
import { ReportLang } from "../../../i18n/reportLang";
import { CONTENT_H } from "../../../whiteLabel/pageMetrics";
import { WhiteLabelTheme } from "../../../whiteLabel/whiteLabelTheme";

import classes from "./WhiteLabelPage.module.css";

interface Props {
  theme: WhiteLabelTheme;
  lang: ReportLang;
  /** Right-hand footer label, already localized. */
  sectionName: string;
  /** Left-hand footer label; "{company}" is replaced with the theme's brand name. */
  brandLabelTemplate: string;
  pageNumber: number;
  children: React.ReactNode;
  dataAgentTarget?: string;
}

/**
 * Themed page shell for white-label reports: brand header, fixed-height content
 * region, brand footer.
 *
 * Wraps Layout rather than extending it — Layout has ~37 consumers across every
 * report, so per-client props there would be a wide blast radius. Layout is
 * unmodified by this feature.
 *
 * Theme values reach leaf CSS as inline custom properties. html2canvas resolves
 * those via getComputedStyle, so it is PDF-safe for CSS properties — but NOT for
 * SVG presentation attributes, which must receive literal hex.
 */
const WhiteLabelPage = ({
  theme,
  lang,
  sectionName,
  brandLabelTemplate,
  pageNumber,
  children,
  dataAgentTarget,
}: Props): JSX.Element => {
  const contentRef = useRef<HTMLDivElement>(null);

  // Layout clips at overflow: hidden without shifting pagination, so content
  // that outgrows the page disappears silently in both the browser and the PDF.
  // Surface it loudly in development instead — this is what makes reviewing the
  // longer Russian and taller Chinese renders tractable.
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    const el = contentRef.current;
    if (!el) return;
    if (el.scrollHeight > Math.ceil(CONTENT_H)) {
      console.warn(
        `[white-label] "${sectionName}" (page ${pageNumber}, lang=${lang}) overflows its page: ` +
          `content is ${el.scrollHeight}px in a ${CONTENT_H}px region — ${
            el.scrollHeight - CONTENT_H
          }px will be clipped.`
      );
    }
  }, [sectionName, pageNumber, lang]);

  // Only the per-brand tokens are injected. The structural palette is declared in
  // WhiteLabelPage.module.css so editors can resolve every var(--wl-*) statically.
  const themeVars = {
    "--wl-primary": theme.colors.primary,
    "--wl-accent-a": theme.colors.accentA,
    "--wl-accent-b": theme.colors.accentB,
    "--wl-marker-yoga": theme.sectionMarkers.yoga,
    "--wl-marker-aerobic": theme.sectionMarkers.aerobic,
    "--wl-marker-strength": theme.sectionMarkers.strength,
  } as React.CSSProperties;

  return (
    <Layout customPadding={0} dataAgentTarget={dataAgentTarget}>
      <div className={classes.page} data-lang={lang} style={themeVars}>
        <header className={classes.header}>
          <img
            className={classes.logo}
            src={theme.logo}
            height={theme.logoHeight}
            alt={theme.companyName}
          />
        </header>

        <div
          ref={contentRef}
          className={classes.content}
          style={{ height: CONTENT_H }}
        >
          {children}
        </div>

        <footer className={classes.footer}>
          <span>
            {brandLabelTemplate.replace("{company}", theme.companyName)}
          </span>
          <span>
            {sectionName} • {pageNumber}
          </span>
        </footer>
      </div>
    </Layout>
  );
};

export default WhiteLabelPage;
